import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cx from 'classnames'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { intersection } from 'lodash'
import localStorageService from 'services/storage'
import { jurisdictionLimits, untaxedIncomeLimits } from 'services/businesses'
import BusinessesSummary from 'components/BusinessesSummary'
import BusinessesActions from 'containers/BusinessesActions'
import BusinessesMap from 'components/BusinessesMap'
import BusinessesFilters from 'components/BusinessesFilters'
import BusinessesList from 'components/BusinessesList'
import GridLoader from 'components/GridLoader'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { selectCurrentZone, selectIsInitialized, selectMapBaseUrl, setIsInitialized } from 'store/global'
import {
  applyFilters,
  configureInitiallySelectedBusinesses,
  fetchBusinesses,
  fetchBusinessesForExport,
  fetchBusinessesSummary,
  fetchBusinessTypes,
  selectAllowScrolling,
  selectBusinesses,
  selectBusinessesForExport,
  selectFetchFromIndex,
  selectFilters,
  selectIsLoadingInitial,
  selectIsLoadingNextBatch,
  selectIsLoadingSummary,
  selectScrollID,
  selectShowMap,
  selectSummary,
  selectTotalBusinesses,
  setIsLoadingInitial,
  setIsLoadingNextBatch,
  setShowMap,
} from 'store/businesses'
import { MAP_CLOSED, MAP_DISPLAYED, MAP_EXPANDED } from 'utils/map'
import { arrayToCsv } from 'utils/csvConverter'
import NavigationHeader from 'components/NavigationHeader/NavigationHeader'
import filtersService from 'services/filters'
import { businessesFilters } from 'utils/filters'
import styles from './Businesses.scss'
import messages from './messages'
import NoDataContainer from 'components/NoDataContainer'

const util = require('util')

export default function Businesses() {
  const dispatch = useDispatch()

  const [exportedBusiness, setExportedBusiness] = useState([])
  const [isShareDisabled, setIsShareDisabled] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [search, setSearch] = useState('')
  const [searchFields, setSearchFields] = useState([])

  const filters = useSelector(selectFilters)
  const isInitialized = useSelector(selectIsInitialized)
  const currentZone = useSelector(selectCurrentZone)

  const businesses = useSelector(selectBusinesses)
  const fromIndex = useSelector(selectFetchFromIndex)
  const allowScrolling = useSelector(selectAllowScrolling)
  const isLoadingSummary = useSelector(selectIsLoadingSummary)
  const isLoadingInitial = useSelector(selectIsLoadingInitial)
  const isLoadingNextBatch = useSelector(selectIsLoadingNextBatch)
  const totalBusinesses = useSelector(selectTotalBusinesses)
  const businessesForExport = useSelector(selectBusinessesForExport)
  const scrollId = useSelector(selectScrollID)
  const mapBaseUrl = useSelector(selectMapBaseUrl)
  const summary = useSelector(selectSummary)
  const showMap = useSelector(selectShowMap)

  const [isMapUrlInitialized, setMapUrlInititalized] = useState(mapBaseUrl !== '')

  const { trackPageView, trackEvent } = useMatomo()

  const refetch = updatedFilters => {
    dispatch(setIsLoadingInitial({ isLoading: true }))
    dispatch(configureInitiallySelectedBusinesses())
    const nextFilters = updatedFilters || filters
    const payload = { filters: { ...nextFilters } }
    dispatch(fetchBusinesses(payload))
    dispatch(fetchBusinessTypes(payload))
  }

  useEffect(() => {
    dispatch(setIsLoadingInitial({ isLoading: false }))
    dispatch(setIsInitialized(true))
  }, [businesses])

  useEffect(() => {
    if (!isInitialized) return
    if (businesses.length) return

    trackPageView()
  }, [isInitialized])

  useEffect(() => {
    setMapUrlInititalized(mapBaseUrl !== '')
  }, [mapBaseUrl])

  useEffect(() => {
    if (!currentZone) return
    const resetedFilters = filtersService.resetFilters(filters, businessesFilters)
    dispatch(applyFilters({ filters: resetedFilters }))
    refetch(resetedFilters)
    dispatch(fetchBusinessesSummary())
  }, [currentZone])

  const onToggleDisplayMap = () => {
    dispatch(
      setShowMap({
        showMap: showMap === MAP_CLOSED ? MAP_DISPLAYED : MAP_CLOSED,
      }),
    )
  }

  const onToggleExpandMap = () => {
    dispatch(
      setShowMap({
        showMap: showMap === MAP_EXPANDED ? MAP_DISPLAYED : MAP_EXPANDED,
      }),
    )
  }

  const onFiltersChange = (modifiedFilters = filters) => {
    const nextFilters = { ...modifiedFilters, search, searchFields }
    if (!nextFilters.untaxedIncome) {
      nextFilters.untaxedIncome = {
        ...untaxedIncomeLimits,
      }
    }

    if (!nextFilters.employessInJurisdiction) {
      nextFilters.employessInJurisdiction = {
        ...jurisdictionLimits,
      }
    }

    if (!nextFilters.locationInJurisdiction) {
      nextFilters.locationInJurisdiction = {
        ...jurisdictionLimits,
      }
    }

    dispatch(applyFilters({ filters: nextFilters }))
    refetch(nextFilters)
    localStorageService.businessesFilters = nextFilters
    trackEvent({
      category: 'Business Mapper Filters',
      action: `User requested for new filters ${util.inspect(nextFilters, {
        showHidden: false,
        depth: null,
      })}`,
    })
  }

  const onLoadMore = () => {
    if (!allowScrolling || isLoadingNextBatch) return

    dispatch(setIsLoadingNextBatch({ isLoading: true }))
    dispatch(fetchBusinesses({ filters, from: fromIndex }))
  }

  const onFieldsChange = nextSearchFields => {
    if (!filters.search) return

    const fieldsDiff = intersection(filters.searchFields, nextSearchFields)

    const isSelectionsTheSame =
      fieldsDiff.length === filters.searchFields.length && filters.searchFields.length === nextSearchFields.length
    if (isSelectionsTheSame) return

    onFiltersChange({ ...filters, searchFields: nextSearchFields })
  }

  useEffect(() => {
    if (businessesForExport.length > 0) {
      const validBusinesses = businessesForExport.filter(business => business != null)
      console.log('Adding valid businesses to export:', validBusinesses.length)
      setExportedBusiness([...exportedBusiness, ...validBusinesses])

      if (scrollId) {
        console.log('Continuing scroll with scrollId:', scrollId)
        dispatch(fetchBusinessesForExport({ filters, scrollId }))
      }
      return
    }

    if (businessesForExport.length === 0 && !scrollId) {
      console.log('Export complete, processing final data')
      if (exportedBusiness.length === 0) {
        console.log('No businesses to export')
        setIsShareDisabled(false)
        return
      }
      const validBusinesses = exportedBusiness.filter(business => business != null)
      console.log('Final valid businesses count:', validBusinesses.length)
      if (validBusinesses.length > 0) {
        arrayToCsv(validBusinesses)
      }
      setExportedBusiness([])
      setIsShareDisabled(false)
      return
    }
  }, [businessesForExport, scrollId])

  const onCSVExport = () => {
    trackEvent({ category: 'Export', action: 'User requested for CSV' })
    setIsShareDisabled(true)
    dispatch(fetchBusinessesForExport({ filters, undefined }))
  }

  const containerRef = useRef(null)
  const navigationRef = useRef(null)
  const headerRef = useRef(null)
  const summaryRef = useRef(null)

  const hasNoData = useMemo(() => {
    return isInitialized && !isLoadingInitial && !isLoadingSummary && (!businesses || businesses.length === 0) && !search
  }, [isInitialized, isLoadingInitial, isLoadingSummary, businesses, search])

  return (
    <div ref={containerRef} className={styles.container}>
      <nav ref={navigationRef} className={styles.navigationHeaderWrapper}>
        <NavigationHeader />
      </nav>
      <header ref={headerRef} className='page-header withNavigationHeader'>
        <h1 className={cx('page-title', 'h1')}>
          <FormattedMessage {...messages.header} />
        </h1>
      </header>
      {hasNoData ? (
        <NoDataContainer verticalName="Business Mapper" zoneName={currentZone?.name} />
      ) : (
        <main className={styles.main}>
          <section ref={summaryRef} className={styles.summary}>
            <BusinessesSummary isLoading={isLoadingSummary} summary={summary} />
          </section>
          <div
            className={cx(styles.contentWrapper, {
              [styles.withFilters]: showFilters,
            })}
          >
            {isInitialized && (
              <BusinessesFilters
                filters={filters}
                onChange={onFiltersChange}
                onClose={() => setShowFilters(false)}
                className={cx(styles.filters, { [styles.hidden]: !showFilters })}
              />
            )}
            <div className={styles.content}>
              <div className={styles.gridContainer}>
                <section className={styles.gridHeader}>
                  <h3 className={styles.count}>
                    <FormattedMessage
                      {...messages.count}
                      values={{
                        displayed: <span className={styles.displayed}>1 - {businesses.length}</span>,
                        total: (
                          <span className={styles.total}>
                            <FormattedNumber value={totalBusinesses} />
                          </span>
                        ),
                      }}
                      tagName='span'
                    />
                  </h3>
                  <Button buttonType='primary' onClick={() => setShowFilters(!showFilters)} className={styles.filter}>
                    <Icon name='filters' size='regular'>
                      <FormattedMessage {...messages.filters} />
                    </Icon>
                  </Button>
                  <span className='dashed-line' />
                  <BusinessesActions
                    onDisplayMap={onToggleDisplayMap}
                    onFieldsChange={onFieldsChange}
                    search={search}
                    setSearch={setSearch}
                    searchFields={searchFields}
                    setSearchFields={setSearchFields}
                    onSearch={onFiltersChange}
                    onExport={onCSVExport}
                    isShareDisabled={isShareDisabled}
                    showMap={showMap}
                  />
                </section>
                <section className={styles.grid}>
                  {isLoadingInitial ? (
                    <GridLoader />
                  ) : (
                    <BusinessesList
                      isLoadingNextBatch={isLoadingNextBatch}
                      businesses={businesses}
                      onScroll={onLoadMore}
                      containerRef={containerRef}
                      navigationRef={navigationRef}
                      headerRef={headerRef}
                      summaryRef={summaryRef}
                    />
                  )}
                </section>
              </div>
              <section
                className={cx(styles.map, {
                  [styles.hidden]: !showMap,
                  [styles.expanded]: showMap === MAP_EXPANDED,
                })}
              >
                {isMapUrlInitialized && (
                  <BusinessesMap
                    isExpanded={showMap === MAP_EXPANDED}
                    onToggleExpand={onToggleExpandMap}
                    onClose={onToggleDisplayMap}
                  />
                )}
              </section>
            </div>
          </div>
        </main>
      )}
    </div>
  )
}
